// src/utils/charterWindow.ts
// 🔥 CHARTER WINDOW (v6.196) — single source of truth for charter check-in /
// check-out window logic. Every "today" comparison is computed in Europe/Athens
// local time, so the window flips at Athens midnight regardless of device TZ.

const ATHENS_TZ = 'Europe/Athens';

/** Today as YYYY-MM-DD in Europe/Athens. */
export function athensTodayYmd(now: Date = new Date()): string {
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ATHENS_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(now);
}

/** Parse a YYYY-MM-DD(...) string to a local-midnight Date (TZ-safe). */
export function parseLocalYmd(s: any): Date {
  if (!s) return new Date(NaN);
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (y && m && d) return new Date(y, m - 1, d);
  return new Date(s);
}

/** True when start and end fall on the same calendar day (day charter / μονοήμερος). */
export function isMonohimeros(startStr: any, endStr: any): boolean {
  if (!startStr || !endStr) return false;
  return String(startStr).slice(0, 10) === String(endStr).slice(0, 10);
}

export interface CharterWindow {
  checkInDate: Date;
  checkOutDate: Date;
  today: Date;
  monohimeros: boolean;
  isBeforeCheckIn: boolean;
  isCheckInDay: boolean;
  isBetweenCheckInAndCheckOut: boolean;
  isCheckOutDay: boolean;
  isCheckOutWindow: boolean;   // endDate-1 OR endDate (or the single day if monohimeros)
  isAfterCheckOut: boolean;
}

/** Compute the charter window relative to Athens "today". */
export function getCharterWindow(startStr: any, endStr: any, now: Date = new Date()): CharterWindow {
  const today = parseLocalYmd(athensTodayYmd(now));
  const checkInDate = parseLocalYmd(startStr);
  const checkOutDate = parseLocalYmd(endStr);
  const ci = checkInDate.getTime();
  const co = checkOutDate.getTime();
  const td = today.getTime();
  const monohimeros = isMonohimeros(startStr, endStr);

  const dayBefore = !isNaN(co)
    ? new Date(checkOutDate.getFullYear(), checkOutDate.getMonth(), checkOutDate.getDate() - 1).getTime()
    : NaN;
  const isCheckInDay = !isNaN(ci) && td === ci;
  const isCheckOutDay = !isNaN(co) && td === co;
  // Check-out window = last 2 days (endDate-1 OR endDate). A day charter's single
  // day is also its check-out window.
  const isCheckOutWindow = isCheckOutDay
    || (!isNaN(dayBefore) && td === dayBefore)
    || (monohimeros && isCheckInDay);

  return {
    checkInDate, checkOutDate, today,
    monohimeros,
    isBeforeCheckIn: !isNaN(ci) && td < ci,
    isCheckInDay,
    isBetweenCheckInAndCheckOut: !isNaN(ci) && !isNaN(co) && td >= ci && td < co,
    isCheckOutDay,
    isCheckOutWindow,
    isAfterCheckOut: !isNaN(co) && td > co,
  };
}

/** Permanent lock: charter fully over (Athens today past the check-out day). */
export function isCharterFullyLocked(startStr: any, endStr: any, now: Date = new Date()): boolean {
  return getCharterWindow(startStr, endStr, now).isAfterCheckOut;
}

/**
 * Returns true if now is PAST (checkout date + checkout time + graceHours) in Europe/Athens.
 * Used to keep check-out editable until graceHours after charter end, even after submit.
 * If date/time missing or invalid, returns false (fail-open: stays editable).
 */
export function isPastCheckoutGrace(
  endDateStr: string | undefined | null,
  checkOutTime: string | undefined | null,
  graceHours: number = 1,
  now: Date = new Date()
): boolean {
  if (!endDateStr) return false;
  const time = (checkOutTime && /^\d{1,2}:\d{2}/.test(checkOutTime)) ? checkOutTime : '09:00';
  const [hh, mm] = time.split(':').map((x) => parseInt(x, 10));
  if (isNaN(hh) || isNaN(mm)) return false;
  // Build "now" in Athens as comparable parts
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: ATHENS_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts: any = {};
  for (const p of fmt.formatToParts(now)) { if (p.type !== 'literal') parts[p.type] = p.value; }
  const nowAthens = new Date(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    parseInt(parts.hour === '24' ? '0' : parts.hour, 10),
    parseInt(parts.minute, 10), 0, 0
  );
  // Build deadline = checkout date + time + graceHours (as local wall-clock, same basis as nowAthens)
  const [y, mo, d] = endDateStr.split('-').map((x) => parseInt(x, 10));
  if (isNaN(y) || isNaN(mo) || isNaN(d)) return false;
  const deadline = new Date(y, mo - 1, d, hh, mm, 0, 0);
  deadline.setHours(deadline.getHours() + graceHours);
  return nowAthens.getTime() >= deadline.getTime();
}
